/**
 * Ingresos y gastos: el mes y lo que se apunta en él.
 *
 * VOCABULARIO, porque la pantalla y la base no lo llaman igual por accidente:
 *  · CIERRE (`MonthlyClose`) es un mes de la organización. Nace abierto al mirarlo y se
 *    concilia una vez. Si al conciliarlo faltaba algo, no queda «conciliado» sino
 *    «incompleto», que es lo que mantiene mayo avisando cuando ya estás en junio.
 *  · APUNTE (`ExpenseEntry`) es UN gasto de UN inmueble dentro de ese mes.
 *
 * EL IMPORTE VIAJA SIEMPRE EN POSITIVO. El guion de «-320,00 €» lo pone la pantalla. Meter el
 * signo en el dato obliga a acordarse de él en cada suma.
 */
import { z } from 'zod';
import { zIsoDate, zMoneyPositive, zOptionalText, zText, zUuid } from '../validation/primitives';

/** `YYYY-MM`. Es como se pide un mes en toda la API. */
export const zMonthKey = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'El mes tiene que ser `AAAA-MM`.');

export const MONTHLY_CLOSE_STATUSES = ['abierto', 'incompleto', 'conciliado'] as const;
export type MonthlyCloseStatus = (typeof MONTHLY_CLOSE_STATUSES)[number];

export const EXPENSE_ENTRY_STATUSES = ['borrador', 'confirmado'] as const;
export type ExpenseEntryStatus = (typeof EXPENSE_ENTRY_STATUSES)[number];

/**
 * Los gastos GENERALES (de ningún inmueble) viajan como un «inmueble» más en la pantalla, con
 * este identificador, para poder elegirlos en los desplegables y soltar tickets en su tarjeta.
 * En la base son `property_id IS NULL`; al subir un ticket se manda `propertyId=general`.
 */
export const GASTOS_GENERALES_ID = 'general';
export const GASTOS_GENERALES_NOMBRE = 'Gastos generales';

export const MONTHLY_CLOSE_STATUS_LABELS: Record<MonthlyCloseStatus, string> = {
  abierto: 'Abierto',
  incompleto: 'Incompleto',
  conciliado: 'Conciliado',
};

/**
 * Alta de un apunte.
 *
 * `concept` e `issuedAt` son obligatorios y el importe también: un gasto sin importe no suma,
 * y un mes que no suma no se puede cuadrar. Lo que SÍ puede faltar es la factura — se apunta
 * el gasto que se sabe que existe aunque el papel no haya llegado, y ese hueco es
 * precisamente uno de los que la conciliación señala.
 */
export const expenseEntrySchema = z.object({
  /** `null` = gasto GENERAL, de ningún inmueble (gestoría, comisiones del banco…). */
  propertyId: zUuid.nullable(),
  /**
   * La habitación, cuando el gasto es de una sola (la regularización de suministros que se le
   * devuelve a quien vive en ella). `null` = de todo el piso. Tiene que ser de `propertyId`.
   */
  unitId: zUuid.nullable().optional(),
  concept: zText(160, 'El concepto'),
  /** Quién emitió la factura. Lo rellena el OCR al subir el ticket. */
  vendorName: zOptionalText(120),
  amount: zMoneyPositive,
  issuedAt: zIsoDate,
  /**
   * El periodo que cobra la factura. Los dos o ninguno, y `serviceTo` no antes que
   * `serviceFrom`: lo comprueba la API (un `refine` aquí impediría el `.partial()` de abajo).
   */
  serviceFrom: zIsoDate.nullable().optional(),
  serviceTo: zIsoDate.nullable().optional(),
  categoryId: zUuid.nullable().optional(),
  recurringExpenseId: zUuid.nullable().optional(),
  documentId: zUuid.nullable().optional(),
  notes: zOptionalText(1000),
});

export const updateExpenseEntrySchema = expenseEntrySchema.partial().extend({
  /**
   * Confirmar es un acto explícito, no un efecto de guardar. Un apunte que la app adivinó y
   * nadie miró tiene que poder distinguirse de uno revisado, o la suposición entra en las
   * cuentas del mes como si fuera un dato.
   */
  status: z.enum(EXPENSE_ENTRY_STATUSES).optional(),
});

/**
 * Conciliar el mes.
 *
 * NO exige que esté todo: se puede cerrar un mes al que le falta una factura, porque en la
 * vida real se cierra igual y la factura llega en agosto. Lo que hace la API es dejar
 * constancia de qué faltaba, y por eso el cierre queda `incompleto` en vez de `conciliado`.
 */
export const reconcileMonthSchema = z.object({
  notes: zOptionalText(2000),
});

/**
 * Resolver a mano un movimiento del extracto.
 *
 *   · `tenant`   — este ingreso es de este inquilino. Con `remember`, se guarda una regla para
 *                  que el mismo texto se reconozca solo los meses siguientes.
 *   · `property` — imputarlo a un inmueble sin inquilino. Si es un CARGO, crea el gasto del
 *                  mes en ese inmueble (sin factura: el ticket se adjunta después). Con
 *                  `unitId`, el gasto es de esa habitación.
 *   · `split`    — un CARGO de varios pisos: se reparte a partes iguales y cada piso lleva su
 *                  parte como gasto del mes (la comunidad de dos pisos en un solo recibo).
 *   · `link`     — este cargo paga este gasto ya apuntado.
 *   · `ignore`   — no es de ningún inmueble: un traspaso entre cuentas propias, una comisión.
 *   · `reset`    — deshacer lo anterior y volver a «pendiente».
 */
/** Entre cuántos pisos se puede repartir un cargo como mucho. */
export const MAX_PISOS_REPARTO = 10;

/** Los pisos de un reparto: dos o más, sin repetir. */
export const zPisosReparto = z
  .array(zUuid)
  .min(2, 'Para repartir hacen falta al menos dos pisos.')
  .max(MAX_PISOS_REPARTO, `Como mucho ${MAX_PISOS_REPARTO} pisos.`)
  .refine((ids) => new Set(ids).size === ids.length, 'Hay un piso repetido.');

/**
 * Un importe repartido A PARTES IGUALES en `partes`, en céntimos para que la suma cuadre al
 * céntimo: los que sobran van a los primeros. 54,43 € entre 3 → 18,15 + 18,14 + 18,14. Vive en
 * `shared` porque la pantalla enseña el reparto antes de guardarlo y la API tiene que llegar
 * exactamente a los mismos números.
 */
export function repartirAPartesIguales(total: string | number, partes: number): string[] {
  if (!Number.isInteger(partes) || partes < 1) throw new Error('Hace falta al menos una parte.');
  const centimos = Math.round(Math.abs(Number(total)) * 100);
  const base = Math.floor(centimos / partes);
  const sobran = centimos - base * partes;
  return Array.from({ length: partes }, (_, indice) => ((base + (indice < sobran ? 1 : 0)) / 100).toFixed(2));
}

export const bankMovementActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('tenant'),
    tenantId: zUuid,
    remember: z.boolean().optional(),
    pattern: z.string().trim().max(120, 'Máximo 120 caracteres.').optional(),
    /**
     * Con `remember`: los movimientos de OTROS meses que la regla reconoce y que la persona ha
     * confirmado que cambien (ver `POST /ledger/movements/:id/remember-preview`). Sin el campo,
     * la regla se aplica sola a lo que siga pendiente, como antes.
     */
    applyTo: z.array(zUuid).max(500).optional(),
  }),
  z.object({
    action: z.literal('property'),
    /** Un inmueble o, para un cargo, `general`: un gasto de ningún inmueble (comisiones, préstamo…). */
    propertyId: zUuid.or(z.literal(GASTOS_GENERALES_ID)),
    concept: zOptionalText(160),
    /** El gasto fijo del inmueble que paga este cargo: el apunte lo cubre y cierra su hueco. */
    recurringExpenseId: zUuid.optional(),
    /** La habitación del inmueble de la que es el cargo (una regularización de suministros). */
    unitId: zUuid.optional(),
  }),
  z.object({
    action: z.literal('split'),
    /** Los pisos entre los que se reparte, a partes iguales y en este orden. */
    propertyIds: zPisosReparto,
    concept: zOptionalText(160),
  }),
  z.object({ action: z.literal('link'), expenseEntryId: zUuid }),
  z.object({ action: z.literal('ignore'), note: zOptionalText(500) }),
  z.object({ action: z.literal('reset') }),
]);

/** Antes de recordar: qué texto se recordaría y para quién. */
export const rememberPreviewSchema = z.object({
  tenantId: zUuid,
  pattern: z.string().trim().max(120, 'Máximo 120 caracteres.').optional(),
});

/**
 * Corregir a mano lo que se ve de un movimiento del extracto en la tabla de conciliación:
 * el proveedor, las observaciones y el inmueble. `null` en un texto lo vacía.
 */
export const updateBankMovementDetailsSchema = z.object({
  vendorName: zOptionalText(120),
  notes: zOptionalText(500),
  /** Un inmueble o, para un cargo, `general` (gastos generales). */
  propertyId: zUuid.or(z.literal(GASTOS_GENERALES_ID)).optional(),
  /**
   * La habitación de un CARGO: la de su inmueble (el que viene en `propertyId` o el que ya
   * tiene). `null` la quita: el gasto vuelve a ser de todo el piso.
   */
  unitId: zUuid.nullable().optional(),
  /** Un CARGO de varios pisos: se reparte a partes iguales entre ellos (ver la acción `split`). */
  propertyIds: zPisosReparto.optional(),
  /**
   * Aplicar lo que propuso la IA para este movimiento pendiente, tal cual: el inquilino, el
   * inmueble, el gasto fijo, el gasto general… Es lo que hace «Guardar» en la ficha de una duda
   * cuando no se ha cambiado su propuesta.
   */
  acceptAiProposal: z.boolean().optional(),
  /**
   * QUIÉN PAGA un ingreso: el inquilino al que se imputa (como «Resolver» → inquilino). La
   * pantalla lo trae propuesto por la IA, pero no se guarda hasta que se guarda la fila.
   */
  tenantId: zUuid.optional(),
  /**
   * Con `tenantId`: ponerlo también en los ingresos EXACTAMENTE IGUALES —mismo concepto del banco
   * (que suele llevar el nombre de quien paga) y mismo importe— de cualquier mes abierto, y
   * recordarlo para los extractos que lleguen.
   */
  applyToSame: z.boolean().optional(),
});

/** Las observaciones de un cobro fuera del banco. */
export const updatePaymentNotesSchema = z.object({
  reference: zOptionalText(200),
});

export type ExpenseEntryInput = z.input<typeof expenseEntrySchema>;
export type UpdateExpenseEntryInput = z.input<typeof updateExpenseEntrySchema>;
export type ReconcileMonthInput = z.input<typeof reconcileMonthSchema>;
export type BankMovementActionInput = z.input<typeof bankMovementActionSchema>;
export type RememberPreviewInput = z.input<typeof rememberPreviewSchema>;
export type UpdateBankMovementDetailsInput = z.input<typeof updateBankMovementDetailsSchema>;
export type UpdatePaymentNotesInput = z.input<typeof updatePaymentNotesSchema>;
